import { useState, useEffect, useRef } from "react";
import { Button } from "@/common/components/ui/button";
import { LineChart, CandlestickChart } from "lucide-react";
import TradingViewChart from "@/common/components/market/TradingViewChart";
import {
  NSEDataItem,
  BSEDataItem,
  CompanyFinancialData,
} from "@/utils/types/Screener/Screener";
import ScreenerService from "@/services/Screener/APIs/ScreenerService";

interface OverviewTabProps {
  company: any;
  nseData: NSEDataItem[] | null;
  nseLoading?: boolean;
  nseError?: string | null;
  nsePreviousClose?: number | null;
  activeExchange?: "NSE" | "BSE";
  exchangeData?: NSEDataItem | BSEDataItem | null;
  exchangeHistory?: any[];
  onExchangeChange?: (ex: "NSE" | "BSE") => void;
}

const OverviewTab = ({
  company,
  activeExchange = "NSE",
  exchangeData: propExchangeData,
  exchangeHistory: propExchangeHistory = [],
  nsePreviousClose,
  onExchangeChange,
}: OverviewTabProps) => {
  const [priceTimeRange, setPriceTimeRange] = useState("1D");
  const [chartType, setChartType] = useState<"line" | "candle">("line");
  const [ttmData, setTtmData] = useState<CompanyFinancialData | null>(null);
  const [ttmLoading, setTtmLoading] = useState(false);
  const [ttmError, setTtmError] = useState<string | null>(null);
  const ttmParamsRef = useRef<string | null>(null);
  // Removed internal exchange state as it's now handled by parent

  useEffect(() => {
    // Generate initial historical data (no longer needed for TradingViewChart)
  }, [company?.id, company?.price]);

  // Fetch TTM data
  useEffect(() => {
    if (!company?.co_code && !company?.id) {
      setTtmData(null);
      return;
    }

    const coCode = company.co_code ?? company.id;
    if (!coCode) {
      return;
    }

    const fetchTTMData = async () => {
      const currentCoCode = coCode.toString();

      // Prevent duplicate fetches for the same company
      if (ttmParamsRef.current === currentCoCode) {
        return;
      }
      ttmParamsRef.current = currentCoCode;

      try {
        setTtmLoading(true);
        setTtmError(null);
        const response = await ScreenerService.getTTMData(coCode.toString());

        if (response?.success) {
          setTtmData(response.data[0] ?? null);
        } else {
          setTtmError("Failed to fetch TTM data");
          setTtmData(null);
        }
      } catch (err) {
        console.error("Error fetching TTM data:", err);
        setTtmError("Failed to fetch TTM data");
        setTtmData(null);
      } finally {
        setTtmLoading(false);
      }
    };

    fetchTTMData();
  }, [company?.co_code, company?.id]);

  // Exchange data fetching removed; handled by parent CompanyDetailsPage

  // Simulation updates removed as we use live exchange history
  useEffect(() => {}, [priceTimeRange, company?.price]);

  if (!company) {
    return null;
  }

  return (
    <div className="max-w-8xl mx-auto space-y-12 duration-500 animate-in fade-in">
      {/* Price Chart Section */}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {["1D", "1W", "1M", "1Y", "5Y", "All"].map(range => (
                <Button
                  key={range}
                  variant="ghost"
                  size="sm"
                  className={`h-8 rounded-full px-3 text-xs font-semibold ${priceTimeRange === range || (range === "1W" && priceTimeRange === "5D") ? "bg-[#00D09C]/10 text-[#00D09C]" : "text-gray-500 hover:bg-gray-50"}`}
                  onClick={() =>
                    setPriceTimeRange(
                      range === "1W" ? "5D" : range === "All" ? "MAX" : range
                    )
                  }
                >
                  {range}
                </Button>
              ))}
            </div>
            <div className="flex rounded-md bg-gray-100 p-1">
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 px-2 ${chartType === "line" ? "bg-white shadow-sm" : "text-gray-500"}`}
                onClick={() => setChartType("line")}
              >
                <LineChart className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 px-2 ${chartType === "candle" ? "bg-white shadow-sm" : "text-gray-500"}`}
                onClick={() => setChartType("candle")}
              >
                <CandlestickChart className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="rounded-xl border border-gray-100 bg-white p-4">
            {propExchangeHistory.length > 0 ? (
              <TradingViewChart
                symbol={activeExchange}
                companyName={company.name ?? company.companyname ?? ""}
                data={propExchangeHistory}
                previousClose={nsePreviousClose}
                timeRange={priceTimeRange}
                chartType={chartType}
                height={400}
              />
            ) : (
              <div className="flex h-[400px] items-center justify-center text-gray-400">
                <p className="text-sm">Loading market data...</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Performance</h3>
              <div className="flex rounded-lg bg-gray-100 p-0.5">
                <button
                  onClick={() => onExchangeChange?.("NSE")}
                  className={`rounded-md px-3 py-1 text-xs font-bold transition-all ${
                    activeExchange === "NSE"
                      ? "bg-white text-[#0c4a6e] shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  NSE
                </button>
                <button
                  onClick={() => onExchangeChange?.("BSE")}
                  className={`rounded-md px-3 py-1 text-xs font-bold transition-all ${
                    activeExchange === "BSE"
                      ? "bg-white text-[#0c4a6e] shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  BSE
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {(() => {
                const calculatePosition = (
                  current: number,
                  low: number,
                  high: number
                ) => {
                  if (
                    isNaN(current) ||
                    isNaN(low) ||
                    isNaN(high) ||
                    high <= low
                  ) {
                    return 50;
                  }
                  const position = ((current - low) / (high - low)) * 100;
                  return Math.min(Math.max(position, 0), 100);
                };

                const currentPrice = Number(
                  propExchangeData?.price ?? company.price
                );
                const todayLow = Number(
                  propExchangeData?.low ?? company.price * 0.98
                );
                const todayHigh = Number(
                  propExchangeData?.high ?? company.price * 1.02
                );
                const todayPosition = calculatePosition(
                  currentPrice,
                  todayLow,
                  todayHigh
                );

                const weekLow = Number(
                  company.weekLow52 ?? company.price * 0.7
                );
                const weekHigh = Number(
                  company.weekHigh52 ?? company.price * 1.3
                );
                const weekPosition = calculatePosition(
                  currentPrice,
                  weekLow,
                  weekHigh
                );

                return (
                  <>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-medium text-gray-500">
                        <span>Today&apos;s Low</span>
                        <span>Today&apos;s High</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-semibold text-gray-900">
                          ₹{todayLow.toFixed(2)}
                        </span>
                        <div className="relative h-1.5 flex-1 rounded-full bg-gray-100">
                          <div
                            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#00D09C] bg-white shadow-md transition-all duration-500"
                            style={{ left: `${todayPosition}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">
                          ₹{todayHigh.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs font-medium text-gray-500">
                        <span>52-Week Low</span>
                        <span>52-Week High</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm font-semibold text-gray-900">
                          ₹{weekLow.toFixed(2)}
                        </span>
                        <div className="relative h-1.5 flex-1 rounded-full bg-gray-100">
                          <div
                            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#00D09C] bg-white shadow-md transition-all duration-500"
                            style={{ left: `${weekPosition}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">
                          ₹{weekHigh.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <div className="space-y-4 border-t border-gray-100 pt-6">
            <div className="grid grid-cols-2 gap-x-8 gap-y-6">
              <div>
                <p className="mb-1 text-xs font-medium text-gray-400">Open</p>
                <p className="text-sm font-semibold text-gray-900">
                  ₹
                  {propExchangeData?.open_price ??
                    (company.price * 0.99).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-gray-400">
                  Prev. Close
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  ₹
                  {company.price
                    ? (
                        company.price /
                        (1 + (company.change ?? 0) / 100)
                      ).toFixed(2)
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-gray-400">Volume</p>
                <p className="text-sm font-semibold text-gray-900">
                  {propExchangeData?.volume
                    ? (Number(propExchangeData.volume) / 100000).toFixed(2)
                    : (company.volume / 100000).toFixed(2)}
                  L
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-gray-400">
                  Avg. Traded Price
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  ₹
                  {propExchangeData?.price ??
                    (company.price * 1.002).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FUNDAMENTALS SECTION */}
      <div className="mb-12 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Fundamentals</h3>
          {ttmLoading && (
            <span className="text-xs text-gray-500">Loading TTM data...</span>
          )}
          {ttmError && <span className="text-xs text-red-500">{ttmError}</span>}
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-6 md:grid-cols-3 lg:grid-cols-5">
          {[
            {
              label: "Market Cap",
              value: `₹${(company.marketCap ?? 0).toLocaleString()} Cr`,
            },
            { label: "Market Type", value: company.mcaptype ?? "N/A" },
            {
              label: "Current Price",
              value: `₹${Number(propExchangeData?.price ?? company.price ?? 0).toLocaleString()}`,
            },
            {
              label: "High / Low",
              value: `₹${propExchangeData?.high ?? "N/A"} / ₹${propExchangeData?.low ?? "N/A"}`,
            },
            {
              label: "Stock P/E",
              value: ttmLoading ? "Loading..." : (ttmData?.pe_ttm ?? "N/A"),
            },
            { label: "Book Value", value: `₹${company.bookValue ?? "N/A"}` },
            {
              label: "Dividend Yield",
              value: ttmLoading
                ? "Loading..."
                : `${ttmData?.dividend_yield ?? "N/A"}%`,
            },
            {
              label: "ROCE",
              value: ttmLoading
                ? "Loading..."
                : `${ttmData?.roce_ttm ?? "N/A"}%`,
            },
            {
              label: "ROE",
              value: ttmLoading
                ? "Loading..."
                : `${ttmData?.roe_ttm ?? "N/A"}%`,
            },
            { label: "Face Value", value: `₹${company.faceValue ?? "N/A"}` },
            {
              label: "Price to Book",
              value: ttmLoading ? "Loading..." : (ttmData?.pb_ttm ?? "N/A"),
            },
            { label: "OPM", value: company.opm ?? "N/A" },
            {
              label: "Deliverable Vol",
              value: company.deliveryVolume ?? "45.2%",
            },
            {
              label: "EV / EBITDA",
              value: ttmLoading
                ? "Loading..."
                : (ttmData?.ev_to_ebitda ?? "N/A"),
            },
            {
              label: "EV / Sales",
              value: ttmLoading
                ? "Loading..."
                : (ttmData?.ev_to_sales ?? "N/A"),
            },
            {
              label: "Debt to Equity",
              value: ttmLoading
                ? "Loading..."
                : (ttmData?.debt_to_equity ?? "N/A"),
            },
            {
              label: "Return on Assets",
              value: ttmLoading ? "Loading..." : (ttmData?.roa ?? "N/A"),
            },
            {
              label: "EPS",
              value: ttmLoading ? "Loading..." : `₹${ttmData?.eps ?? "N/A"}`,
            },
            {
              label: "PAT Qtr",
              value: `₹${(company.patQtr ?? "N/A").toLocaleString()} Cr`,
            },
            {
              label: "Qtr Profit Var",
              value: `${company.qtrProfitVar ?? "N/A"}%`,
            },
            {
              label: "Qtr Sales Var",
              value: `${company.qtrSalesVar ?? "N/A"}%`,
            },
          ].map(item => (
            <div key={item.label}>
              <p className="mb-1 text-xs font-medium text-gray-500">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ABOUT COMPANY SECTION */}
      <div className="space-y-6">
        <h3 className="text-lg font-bold text-gray-900">About Company</h3>
        <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <p className="text-sm leading-relaxed text-gray-600">
            {company.description ?? "N/A"}
          </p>
          <div className="space-y-3 border-t border-gray-50 pt-4">
            <div className="flex flex-wrap gap-x-12 gap-y-4">
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-500">
                  Market Cap Rank
                </span>
                <span className="text-sm font-bold text-gray-900">N/A</span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-500">
                  Sector
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {company.sector ?? "N/A"}
                </span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-500">
                  Industry
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {company.industry ?? company.sector ?? "N/A"}
                </span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-500">
                  CEO
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {company.ceo ?? "N/A"}
                </span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-500">
                  Headquarters
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {company.headquarters ?? "N/A"}
                </span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-500">
                  Website
                </span>
                <a
                  href={`https://${company.website ?? "www.example.com"}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-bold text-[#00D09C] hover:underline"
                >
                  {company.website ?? "N/A"}
                </a>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-500">
                  Incorporation Date
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {company.incorporationDate ?? "N/A"}
                </span>
              </div>
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-500">
                  ISIN
                </span>
                <span className="text-sm font-bold text-gray-900">
                  {company.isin ?? "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
